// store/auth.ts - COMPLETE ENHANCED VERSION with updated API integration and fixed login
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '@/api';
import { normalizeUser } from '@/api/types'; // Import normalizeUser from types.ts
import type { 
  User, 
  NormalizedUser, 
  AuthResponse, 
  UserUpdateRequest,
  LoginCredentials,
  CurrentUserResponse,
  TokenVerificationResponse,
  Group
} from '@/api/types';

// ===== ENHANCED LOGIN CREDENTIALS INTERFACE =====
interface ExtendedLoginCredentials extends LoginCredentials {
  username: string;
  password: string;
  organization: string;
}

// ===== TYPE DEFINITIONS =====

interface AuthState {
  // Core authentication state
  user: NormalizedUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Session management
  lastActivity: number;
  sessionExpiry: number | null;
  sessionWarningShown: boolean;
  
  // Profile sync
  profileSyncStatus: 'idle' | 'syncing' | 'failed' | 'success';
  profileSyncError: string | null;
  
  // User validation
  userValidationStatus: 'valid' | 'invalid' | 'unknown';
  
  // API integration tracking
  apiCallCount: number;
  lastSuccessfulSync: number | null;
}

interface AuthActions {
  // Authentication
  login: (username: string, organization: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<void>;
  refreshAuth: () => Promise<void>;
  
  // Token management
  setTokens: (token: string, refreshToken: string) => void;
  isTokenExpired: () => boolean;
  
  // User management
  setUser: (user: User | NormalizedUser) => void;
  fetchUser: () => Promise<void>;
  syncUserProfile: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  
  // Authentication checks
  checkAuth: () => Promise<boolean>;
  checkAuthQuick: () => boolean;
  validateCurrentUser: () => boolean;
  
  // Session management
  updateActivity: () => void;
  checkSession: () => boolean;
  extendSession: () => void;
  showSessionWarning: () => void;
  clearSessionWarning: () => void;
  
  // Error handling
  clearError: () => void;
  setError: (error: string) => void;
  
  // Utilities
  getSafeUser: () => NormalizedUser | null;
  getOrganizationName: () => string;
  getUserGroups: () => Array<{ _id: string; name: string; type: 'visibility' | 'approval' | 'system' }>;
  hasRole: (role: 'admin' | 'contributor' | 'implementor') => boolean;
  isInGroup: (groupId: string) => boolean;
  
  // System
  reset: () => void;
  incrementApiCall: () => void;
}

type AuthStore = AuthState & AuthActions;

// ===== LOCAL HELPER FUNCTIONS (not in types.ts) =====

const validateUserData = (user: any): boolean => {
  if (!user || typeof user !== 'object') {
    return false;
  }

  const requiredFields = ['_id', 'username', 'role'];
  for (const field of requiredFields) {
    if (!user[field]) {
      console.warn(`User validation failed: missing ${field}`);
      return false;
    }
  }

  if (!['user', 'admin', 'contributor', 'implementor'].includes(user.role)) {
    console.warn(`User validation failed: invalid role ${user.role}`);
    return false;
  }

  return true;
};

const validatePersistenceData = (data: any): boolean => {
  if (!data || typeof data !== 'object') {
    console.warn('Persisted auth: Invalid data structure');
    return false;
  }

  if (!data.token || typeof data.token !== 'string') {
    console.warn('Persisted auth: Missing or invalid token');
    return false;
  }

  if (data.sessionExpiry && typeof data.sessionExpiry === 'number') {
    if (Date.now() > data.sessionExpiry) {
      console.warn('Persisted auth: Session expired');
      return false;
    }
  }

  if (data.user) {
    try {
      if (!data.user.username || !data.user.role) {
        console.warn('Persisted auth: Invalid user data structure');
        return false;
      }
      return true;
    } catch (error: any) {
      console.warn('Persisted auth: Error validating user data:', error?.message || 'Unknown error');
      return false;
    }
  }

  return true;
};

// ===== INITIAL STATE =====

const initialState: AuthState = {
  // Core authentication
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  
  // Session management
  lastActivity: Date.now(),
  sessionExpiry: null,
  sessionWarningShown: false,
  
  // Profile sync
  profileSyncStatus: 'idle',
  profileSyncError: null,
  
  // User validation
  userValidationStatus: 'unknown',
  
  // API integration tracking
  apiCallCount: 0,
  lastSuccessfulSync: null,
};

// ===== STORE IMPLEMENTATION =====

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ===== AUTHENTICATION METHODS =====

      login: async (username: string, organization: string, password: string) => {
        try {
          set({ 
            isLoading: true, 
            error: null, 
            userValidationStatus: 'unknown',
            sessionWarningShown: false
          });
          
          
          const loginPayload: LoginCredentials = { 
            username, 
            password,
            organization 
          };
          
          const response = await apiClient.auth.login(loginPayload);

          
          // CRITICAL FIX: Check for response structure from backend
          // The backend is returning the data correctly, check for proper fields
          
          if (!response.user) {
            console.error('❌ No user data in response:', response);
            throw new Error('No user data received from server');
          }

          if (!response.accessToken) {
            console.error('❌ No access token in response:', response);
            throw new Error('No authentication token received from server');
          }

          if (!validateUserData(response.user)) {
            console.error('❌ Invalid user data:', response.user);
            throw new Error('Invalid user data received from server');
          }

          const token = response.accessToken;
          const refreshToken = response.refreshToken || '';
          const normalizedUser = normalizeUser(response.user as User);
          
          // Calculate session expiry (1 hour from now, or use server-provided expiry)
          const sessionExpiry = response.expiresIn 
            ? Date.now() + (response.expiresIn * 1000)
            : Date.now() + (60 * 60 * 1000); // 1 hour default

          set({
            user: normalizedUser,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            sessionExpiry,
            lastActivity: Date.now(),
            userValidationStatus: 'valid',
            lastSuccessfulSync: Date.now(),
            profileSyncStatus: 'success'
          });

          
        } catch (error: any) {
          console.error('❌ Login failed:', error);
          
          let errorMessage = 'Login failed';
          
          // Handle specific auth error types
          if (error.response?.status === 401) {
            errorMessage = 'Invalid username, organization, or password';
          } else if (error.response?.status === 403) {
            errorMessage = 'Your account has been deactivated. Please contact your administrator.';
          } else if (error.response?.status === 404) {
            errorMessage = 'User not found in the specified organization';
          } else if (error.response?.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.message && error.message !== 'Login failed') {
            errorMessage = error.message;
          }
          
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
            userValidationStatus: 'invalid',
            profileSyncStatus: 'failed',
            profileSyncError: errorMessage
          });
          
          throw new Error(errorMessage);
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true, error: null });


          // Call logout API if authenticated
          const { token } = get();
          if (token) {
            try {
              await apiClient.auth.logout();
            } catch (error) {
              console.warn('Logout API call failed, but continuing with local logout:', error);
            }
          }

          // Clear state and storage
          set({
            ...initialState,
            lastActivity: Date.now()
          });

          // Clear ALL auth-related localStorage keys to prevent session contamination
          const keysToRemove = [
            'authToken',
            'refreshToken',
            'user',
            'userId',
            'organizationId',
            'organizationName',
            'userOrganization',
            'userRole',
            'oauth_state',
            'auth-storage',
            'app-storage'
          ];

          keysToRemove.forEach(key => localStorage.removeItem(key));

          // Clear sessionStorage as well
          sessionStorage.clear();


        } catch (error: any) {
          console.error('❌ Logout error:', error);
          
          // Even if logout fails, clear local state
          set({
            ...initialState,
            lastActivity: Date.now(),
            error: 'Logout completed with errors'
          });
          
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      },

      register: async (userData: any) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await apiClient.auth.register(userData);
          
          // RegistrationResponse doesn't have a success field, just check for message
          if (!response.message) {
            throw new Error('Registration failed');
          }
          
          
        } catch (error: any) {
          console.error('❌ Registration failed:', error);
          
          let errorMessage = 'Registration failed';
          if (error.response?.status === 409) {
            errorMessage = 'User already exists';
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage
          });
          
          throw error;
        }
      },

      refreshAuth: async () => {
        try {
          const { refreshToken: currentRefreshToken } = get();
          
          if (!currentRefreshToken) {
            throw new Error('No refresh token available');
          }
          
          
          const response = await apiClient.auth.refreshToken();
          
          if (!response.accessToken) {
            throw new Error('No access token received from refresh');
          }
          
          const sessionExpiry = Date.now() + (60 * 60 * 1000); // 1 hour
          
          set({
            token: response.accessToken,
            refreshToken: currentRefreshToken, // Keep existing refresh token since RefreshTokenResponse doesn't return a new one
            sessionExpiry,
            lastActivity: Date.now(),
            lastSuccessfulSync: Date.now(),
            error: null,
            sessionWarningShown: false
          });
          
          
        } catch (error: any) {
          console.error('❌ Token refresh failed:', error);
          
          // Clear auth state on refresh failure
          set({
            ...initialState,
            lastActivity: Date.now(),
            error: 'Session expired. Please log in again.',
            userValidationStatus: 'invalid'
          });
          
          throw error;
        }
      },

      // ===== TOKEN MANAGEMENT =====

      setTokens: (token: string, refreshToken: string) => {
        const sessionExpiry = Date.now() + (60 * 60 * 1000); // 1 hour
        
        set({
          token,
          refreshToken,
          sessionExpiry,
          lastActivity: Date.now()
        });
      },

      isTokenExpired: () => {
        const { sessionExpiry } = get();
        return !sessionExpiry || Date.now() > sessionExpiry;
      },

      // ===== USER MANAGEMENT =====

      setUser: (user: User | NormalizedUser) => {
        const normalizedUser = 'id' in user && user.id ? user as NormalizedUser : normalizeUser(user as User);
        
        set({
          user: normalizedUser,
          userValidationStatus: 'valid',
          lastSuccessfulSync: Date.now()
        });
      },

      fetchUser: async () => {
        try {
          set({ 
            profileSyncStatus: 'syncing',
            profileSyncError: null 
          });
          
          
          const response = await apiClient.auth.getCurrentUser();
          
          if (!response || !validateUserData(response)) {
            throw new Error('Invalid user data received from server');
          }
          
          // Convert CurrentUserResponse to User format for normalization
          const userForNormalization: User = {
            _id: response._id,
            id: response.id,
            username: response.username,
            firstName: response.firstName,
            lastName: response.lastName,
            email: response.email,
            role: response.role,
            organization: response.organization._id, // Use organization ID as string
            organizationId: response.organization._id,
            organizationName: response.organization.name,
            groups: response.groups as Group[], // Type assertion since CurrentUserResponse.groups matches Group[]
            department: response.department,
            emailAccounts: response.emailAccounts,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
            lastLoginAt: response.lastLoginAt,
            isActive: response.isActive,
            deactivated: response.deactivated
          };
          
          const normalizedUser = normalizeUser(userForNormalization);
          
          set({
            user: normalizedUser,
            userValidationStatus: 'valid',
            profileSyncStatus: 'success',
            profileSyncError: null,
            lastSuccessfulSync: Date.now()
          });
          
          
        } catch (error: any) {
          console.error('❌ Failed to fetch user data:', error);
          
          const errorMessage = error.message || 'Failed to fetch user data';
          
          set({
            profileSyncStatus: 'failed',
            profileSyncError: errorMessage,
            userValidationStatus: 'invalid'
          });
          
          throw error;
        }
      },

      syncUserProfile: async () => {
        const { fetchUser } = get();
        return fetchUser();
      },

      updateUser: async (userData: Partial<User>) => {
        try {
          const { user } = get();
          
          if (!user) {
            throw new Error('No user data available');
          }
          
          set({ 
            profileSyncStatus: 'syncing',
            profileSyncError: null 
          });
          
          
          const response = await apiClient.users.update(user._id, userData as UserUpdateRequest);
          
          if (!response || !validateUserData(response)) {
            throw new Error('Invalid user data received from update');
          }
          
          const normalizedUser = normalizeUser(response as User);
          
          set({
            user: normalizedUser,
            profileSyncStatus: 'success',
            profileSyncError: null,
            lastSuccessfulSync: Date.now()
          });
          
          
        } catch (error: any) {
          console.error('❌ Failed to update user profile:', error);
          
          const errorMessage = error.message || 'Failed to update user profile';
          
          set({
            profileSyncStatus: 'failed',
            profileSyncError: errorMessage
          });
          
          throw error;
        }
      },

      // ===== AUTHENTICATION CHECKS =====

      checkAuth: async () => {
        try {
          const { user, token, isTokenExpired, refreshAuth, isAuthenticated } = get();
          
          // If already authenticated and have valid user/token, return true immediately
          if (isAuthenticated && user && token) {
            return true;
          }
          
          if (!user || !token) {
            set({ 
              isAuthenticated: false,
              userValidationStatus: 'invalid'
            });
            return false;
          }
          
          if (isTokenExpired()) {
            
            try {
              await refreshAuth();
              return true;
            } catch (error) {
              set({ 
                isAuthenticated: false,
                userValidationStatus: 'invalid'
              });
              return false;
            }
          }
          
          // Try to verify token with server
          try {
            
            const response = await apiClient.auth.verifyToken();
            
            if (!response.success) {
              return false;
            }
            
            // Update user data if provided in verification response
            if (response.user && validateUserData(response.user)) {
              const normalizedUser = normalizeUser(response.user as any);
              set({
                user: normalizedUser,
                userValidationStatus: 'valid',
                lastSuccessfulSync: Date.now()
              });
            }
            
            return true;
            
          } catch (verifyError: any) {
            
            if (verifyError.response?.status === 401 && verifyError.response?.data?.code === 'TOKEN_EXPIRED') {
              try {
                await refreshAuth();
                return true;
              } catch (refreshError) {
                return false;
              }
            }
            
            return false;
          }
          
        } catch (error: any) {
          console.error('❌ Auth check failed:', error);
          return false;
        }
      },

      checkAuthQuick: () => {
        const { user, token, isAuthenticated, checkSession } = get();
        
        // If already authenticated, trust that state
        if (isAuthenticated && user && token) {
          return true;
        }
        
        // Otherwise do the full check
        return !!(user && token && checkSession());
      },

      validateCurrentUser: () => {
        const { user } = get();
        return !!(user && validateUserData(user));
      },

      // ===== SESSION MANAGEMENT =====

      updateActivity: () => {
        set({ lastActivity: Date.now() });
      },

      checkSession: () => {
        const { sessionExpiry, sessionWarningShown, showSessionWarning } = get();
        
        if (!sessionExpiry) {
          return false;
        }
        
        const now = Date.now();
        const isValid = now < sessionExpiry;
        
        // Show warning if session expires in less than 5 minutes
        if (isValid && !sessionWarningShown) {
          const timeLeft = sessionExpiry - now;
          if (timeLeft < (5 * 60 * 1000)) { // 5 minutes
            showSessionWarning();
          }
        }
        
        return isValid;
      },

      extendSession: () => {
        const sessionExpiry = Date.now() + (60 * 60 * 1000); // Extend by 1 hour
        
        set({
          sessionExpiry,
          lastActivity: Date.now(),
          sessionWarningShown: false
        });
      },

      showSessionWarning: () => {
        set({ sessionWarningShown: true });
      },

      clearSessionWarning: () => {
        set({ sessionWarningShown: false });
      },

      // ===== ERROR HANDLING =====

      clearError: () => {
        set({ 
          error: null,
          profileSyncError: null 
        });
      },

      setError: (error: string) => {
        set({ error });
      },

      // ===== UTILITIES =====

      getSafeUser: () => {
        const { user, isAuthenticated } = get();
        return user && validateUserData(user) ? user : null;
      },

      getOrganizationName: () => {
        const { user } = get();
        return user?.organization?.name || 'Unknown Organization';
      },

      getUserGroups: () => {
        const { user } = get();
        return user?.groups || [];
      },

      hasRole: (role: 'admin' | 'contributor' | 'implementor') => {
        const { user } = get();
        return user?.role === role;
      },

      isInGroup: (groupId: string) => {
        const { user } = get();
        return user?.groups?.some(group => group._id === groupId) || false;
      },

      // ===== SYSTEM =====

      reset: () => {
        set(initialState);
      },

      incrementApiCall: () => {
        const { apiCallCount } = get();
        set({ apiCallCount: apiCallCount + 1 });
      }
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        sessionExpiry: state.sessionExpiry,
        lastActivity: state.lastActivity,
        userValidationStatus: state.userValidationStatus,
        lastSuccessfulSync: state.lastSuccessfulSync
      }),
      onRehydrateStorage: () => (state) => {
        if (state && !validatePersistenceData(state)) {
          console.warn('Invalid persisted auth data, resetting store');
          return {
            ...initialState,
            lastActivity: Date.now()
          };
        }
      }
    }
  )
);

// ===== SESSION MANAGEMENT HOOK =====

export const useAuthSession = () => {
  const {
    checkSession,
    refreshAuth,
    updateActivity,
    validateCurrentUser,
    sessionWarningShown,
    showSessionWarning,
    clearSessionWarning
  } = useAuthStore();

  const trackActivity = () => {
    updateActivity();
  };

  const handleTokenRefresh = async () => {
    try {
      await refreshAuth();
      clearSessionWarning();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const checkSessionWithWarning = () => {
    const { sessionExpiry, sessionWarningShown } = useAuthStore.getState();
    
    if (!sessionExpiry) {
      return false;
    }
    
    const isValid = checkSession();
    
    // Show warning if session expires in less than 5 minutes
    if (isValid && sessionExpiry && !sessionWarningShown) {
      const timeLeft = sessionExpiry - Date.now();
      if (timeLeft < (5 * 60 * 1000)) { // 5 minutes
        showSessionWarning();
      }
    }
    
    return isValid;
  };

  return {
    isSessionValid: checkSessionWithWarning,
    refreshToken: handleTokenRefresh,
    trackActivity,
    validateUser: validateCurrentUser,
    sessionWarningShown
  };
};

// ===== CONVENIENCE HOOKS =====

export const useAuth = () => {
  const store = useAuthStore();
  return {
    // State
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    profileSyncStatus: store.profileSyncStatus,
    profileSyncError: store.profileSyncError,
    userValidationStatus: store.userValidationStatus,
    sessionWarningShown: store.sessionWarningShown,
    
    // Actions
    login: store.login,
    logout: store.logout,
    clearError: store.clearError,
    checkAuth: store.checkAuth,
    checkAuthQuick: store.checkAuthQuick,
    
    // Utilities
    getSafeUser: store.getSafeUser,
    getOrganizationName: store.getOrganizationName,
    getUserGroups: store.getUserGroups,
    hasRole: store.hasRole,
    isInGroup: store.isInGroup,
    validateCurrentUser: store.validateCurrentUser,
  };
};

export const useUser = () => {
  const { user, updateUser, fetchUser, syncUserProfile, getSafeUser, validateCurrentUser } = useAuthStore();
  return { 
    user,
    updateUser, 
    fetchUser,
    syncUserProfile,
    getSafeUser,
    validateCurrentUser
  };
};

export const useAuthToken = () => {
  const { token, refreshToken, setTokens, isTokenExpired } = useAuthStore();
  return { token, refreshToken, setTokens, isTokenExpired };
};

// Export store for direct access when needed
export default useAuthStore;